import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { apiJson } from "../lib/api";
import { pct } from "../lib/stats";
import type { AccountStats } from "../../shared/api-types";

// Key-Zahlen über alle Touren auf der Startseite. Bewusst nur vier Kacheln ohne
// Zeitraumwahl und Verlauf — die Tiefe steckt im Statistik-Tab der einzelnen Tour.

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      <p className="text-sm text-zinc-600 dark:text-zinc-300">{label}</p>
      {hint && <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">{hint}</p>}
    </div>
  );
}

export function DashboardStats() {
  const t = useT();
  const [stats, setStats] = useState<AccountStats | null>(null);

  useEffect(() => {
    let alive = true;
    // Ein Fehler bleibt still: die Zahlen sind Beiwerk, die Tourenliste zählt.
    apiJson<AccountStats>("/api/account/stats")
      .then((s) => alive && setStats(s))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Ohne Aufrufe bliebe nur eine Reihe Nullen — dann lieber gar keine Zahlen.
  if (!stats || stats.views === 0) return null;

  const fmtDuration = (s: number) =>
    s < 60 ? t.stats.seconds(s) : t.stats.minutes(Math.floor(s / 60), s % 60);

  return (
    <section className="mb-8">
      <p className="mb-2 text-xs text-zinc-400 dark:text-zinc-500">{t.stats.overview(stats.days)}</p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label={t.stats.views} value={String(stats.views)} />
        <Tile
          label={t.stats.visitors}
          value={String(stats.visitors)}
          hint={t.stats.visitorsHintAll}
        />
        <Tile
          label={t.stats.avgDuration}
          value={stats.sessions > 0 ? fmtDuration(stats.avgSeconds) : "–"}
          hint={t.stats.avgDurationHint}
        />
        <Tile
          label={t.stats.interactionRate}
          value={stats.sessions > 0 ? `${pct(stats.interacted, stats.sessions)} %` : "–"}
          hint={t.stats.interactionHint}
        />
      </div>
    </section>
  );
}
